/**
 * DeactivateUserDialog
 *
 * Confirms an account deactivation and lets the admin attach an optional
 * reason that is shown to the user on the /account-deactivated landing
 * page when they next attempt to sign in. Reactivation never goes through
 * this dialog — it just fires the toggle directly because there's nothing
 * extra to collect.
 *
 * The dialog is intentionally self-contained: it owns the textarea state
 * and only emits the confirmed reason via `onConfirm`. The caller handles
 * the mutation + toast + cache invalidation.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

interface DeactivateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string | null) => Promise<void> | void;
  isSubmitting?: boolean;
  userName: string;
}

const REASON_MAX = 500;

export default function DeactivateUserDialog({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  userName,
}: DeactivateUserDialogProps) {
  const [reason, setReason] = useState('');

  // Reset the field each time the dialog opens so leftover text from a prior
  // deactivation never bleeds into the next one.
  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  // Lock background scroll while open — matches the other admin modals so
  // the user can't scroll the underlying page through the backdrop.
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    html.style.setProperty('overflow-y', 'hidden', 'important');
    return () => {
      html.style.removeProperty('overflow-y');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    await onConfirm(trimmed.length > 0 ? trimmed.slice(0, REASON_MAX) : null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="relative z-50 w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-xl">
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div>
                <h3 className="text-lg font-semibold">Deactivate account</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {userName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
              aria-label="Close"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-amber-600/40 bg-amber-600/10 px-3 py-2 text-sm text-amber-500">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              They will be logged out immediately and unable to register for
              events, edit their profile, or make payments. Transaction
              history is preserved.
            </span>
          </div>

          <div>
            <label htmlFor="deactivation-reason" className="text-sm font-medium">
              Reason (optional)
            </label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Shown to the user on the deactivated-account landing page when
              they next try to sign in. Leave blank to skip.
            </p>
            <Textarea
              id="deactivation-reason"
              className="mt-2 min-h-[88px]"
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate account — please use your other login at user@example.com"
              disabled={isSubmitting}
            />
            <div className="mt-1 text-right text-xs text-muted-foreground">
              {reason.length}/{REASON_MAX}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Deactivate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
